import React from 'react';
import { AppConfirmDialog } from '../AppConfirmDialog';
import {
  assigneeConflictLines,
  readAssigneeConflict,
  type AssigneeConflictResponse,
} from '../../utils/assigneeConflicts';
import { UI_Z_CLASS } from '../../utils/uiZIndex';

export type ConflictDialogTexts = {
  title: string;
  question: string;
  confirmLabel: string;
};

const ACTIVITY_TEXTS: ConflictDialogTexts = {
  title: 'Einsatzteam nicht verfügbar',
  question: 'Trotzdem speichern?',
  confirmLabel: 'Trotzdem speichern',
};

/** For absences that overlap open activities of the employee. */
export const ABSENCE_CONFLICT_TEXTS: ConflictDialogTexts = {
  title: 'Termine im Abwesenheitszeitraum',
  question: 'Abwesenheit trotzdem eintragen? Die Termine bleiben bestehen.',
  confirmLabel: 'Trotzdem eintragen',
};

/** Mehr Zeilen erst nach „weitere anzeigen“; lange Listen (Serien) sprengen sonst den Dialog. */
const VISIBLE_CONFLICT_LINES = 5;

type Pending = {
  conflict: AssigneeConflictResponse;
  retry: () => Promise<void> | void;
  onCancel?: () => void;
  texts: ConflictDialogTexts;
};

/**
 * "Einsatzteam nicht verfügbar — trotzdem speichern?" for activity saves (texts overridable,
 * e.g. ABSENCE_CONFLICT_TEXTS when an absence overlaps open activities).
 * `intercept(res, retry)` returns true when the response was an assignee conflict; the dialog
 * then calls `retry` (which should resend with `confirm_conflicts: true`) on confirm.
 */
export function useAssigneeConflictConfirm() {
  const [pending, setPending] = React.useState<Pending | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [showAll, setShowAll] = React.useState(false);

  const intercept = React.useCallback(
    async (
      res: Response,
      retry: Pending['retry'],
      onCancel?: () => void,
      texts: ConflictDialogTexts = ACTIVITY_TEXTS,
    ): Promise<boolean> => {
      const conflict = await readAssigneeConflict(res);
      if (!conflict) return false;
      setPending({ conflict, retry, onCancel, texts });
      setShowAll(false);
      return true;
    },
    [],
  );

  const close = React.useCallback(() => {
    if (busy) return;
    pending?.onCancel?.();
    setPending(null);
  }, [busy, pending]);

  const lines = pending ? assigneeConflictLines(pending.conflict) : [];
  const hiddenCount = showAll ? 0 : Math.max(0, lines.length - VISIBLE_CONFLICT_LINES);
  const shownLines = hiddenCount > 0 ? lines.slice(0, VISIBLE_CONFLICT_LINES) : lines;
  const texts = pending?.texts ?? ACTIVITY_TEXTS;
  const dialog = (
    <AppConfirmDialog
      open={pending != null}
      onClose={close}
      title={texts.title}
      tone="warning"
      confirmLabel={texts.confirmLabel}
      isLoading={busy}
      overlayClassName={UI_Z_CLASS.saveConfirm}
      description={
        <div className="space-y-2">
          <ul className="app-scrollbar max-h-60 list-disc space-y-1 overflow-y-auto pl-5">
            {shownLines.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
          {hiddenCount > 0 ? (
            <button
              type="button"
              onClick={() => setShowAll(true)}
              className="text-sm font-medium text-amber-800 underline-offset-2 hover:underline"
            >
              {hiddenCount === 1 ? '1 weiteren anzeigen' : `${hiddenCount} weitere anzeigen`}
            </button>
          ) : null}
          <p>{texts.question}</p>
        </div>
      }
      onConfirm={async () => {
        if (!pending) return;
        setBusy(true);
        try {
          await pending.retry();
        } finally {
          setBusy(false);
          setPending(null);
        }
      }}
    />
  );

  return { intercept, dialog };
}
